import {useEffect, useMemo, useRef} from 'react';
import {useDispatch, useSelector, useStore} from 'react-redux';

import {nextSlide} from '../actions/slides';
import {
  getCurrentDeck,
  getCurrentFolder,
  getCurrentSlide,
  getSlideRotationIsPaused,
} from '../selectors';

// This is just a precautionary value to ensure that a sensible slide duration
// will always be set; it only comes into play if something has gone wrong.
const fallbackSlideDuration = 30000;

export default function useRotator() {
  const dispatch = useDispatch();

  const store = useStore();

  const pauseStartTimestamp = useRef();
  const previousSlideId = useRef();
  const slideStartTimestamp = useRef();
  const timeoutId = useRef();

  const currentDeck = useSelector(getCurrentDeck);
  const currentFolder = useSelector(getCurrentFolder);
  const currentSlide = useSelector(getCurrentSlide);
  const slideRotationIsPaused = useSelector(getSlideRotationIsPaused);

  const currentSlideId = currentSlide && currentSlide.id;

  const slideDuration = useMemo(() => {
    // Deck is needed because durations for specific slides are stored on it.
    if (!currentDeck.id) {
      return null;
    }

    const sceneRefreshRates = currentDeck.options.sceneRefreshRates;

    let slideDuration;

    // If there are slides in the deck with durations set then the slide needs
    // to be checked, in case it's one of them.
    if (sceneRefreshRates && sceneRefreshRates.length) {
      if (!currentSlide) {
        return null;
      }

      const old_id = currentSlide.old_id;
      const refreshRateObj = sceneRefreshRates.find(obj => obj.sceneId === old_id);
      slideDuration = refreshRateObj && refreshRateObj.refreshRate * 1000;
    }

    // If the slide didn't have a specific duration set then the folder is
    // needed to lookup the default slide duration.
    if (!slideDuration) {
      if (!currentFolder) {
        return null;
      }

      slideDuration = currentFolder.options.defaultrotation * 1000;
    }

    return slideDuration;
  }, [
    currentDeck,
    currentFolder,
    currentSlide,
  ]);

  useEffect(() => {
    const now = Date.now();

    // Remember when slide rotation becomes paused.
    if (slideRotationIsPaused && !pauseStartTimestamp.current) {
      pauseStartTimestamp.current = now;
    }

    // When rotation is unpaused, offset the time at which the slide started
    // showing by the duration of the pause, so that the remaining slide time
    // will not be shortened due to the pause.
    if (!slideRotationIsPaused && pauseStartTimestamp.current) {
      slideStartTimestamp.current += now - pauseStartTimestamp.current;
      pauseStartTimestamp.current = null;
    }

    if (currentSlideId && slideDuration !== null && !slideRotationIsPaused) {
      const hasChangedSlide = currentSlideId !== previousSlideId.current;

      // Remember when this new slide started to show.
      if (hasChangedSlide) {
        slideStartTimestamp.current = now;
      }

      // Determine how long the current slide has left to show for.
      const elapsedTime = now - slideStartTimestamp.current;
      const remainingDuration = Math.max(0, slideDuration - elapsedTime);

      // Schedule the slide change.
      timeoutId.current = setTimeout(() => {
        nextSlide()(dispatch, store.getState);
      }, isNaN(remainingDuration) ? fallbackSlideDuration : remainingDuration);

      // Remember the slide which is showing.
      previousSlideId.current = currentSlideId;
    }

    return () => {
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
        timeoutId.current = null;
      }
    };
  }, [
    currentSlideId,
    dispatch,
    slideDuration,
    slideRotationIsPaused,
    store.getState
  ]);
}
